import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface ResearchOverviewFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

interface StructuredSummary {
  background_social: string;
  background_academic: string;
  background_personal: string;
  purpose_what: string;
  purpose_how: string;
  purpose_scope: string;
  intervention_group: string;
  control_group: string;
  significance_academic: string;
  significance_social: string;
  significance_practical: string;
}

const defaultSummary: StructuredSummary = {
  background_social: '',
  background_academic: '',
  background_personal: '',
  purpose_what: '',
  purpose_how: '',
  purpose_scope: '',
  intervention_group: '',
  control_group: '',
  significance_academic: '',
  significance_social: '',
  significance_practical: '',
};

function parseSummary(raw: string | null): StructuredSummary {
  if (!raw) return { ...defaultSummary };
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return { ...defaultSummary, ...parsed };
    }
    return { ...defaultSummary };
  } catch {
    // プレーンテキストの場合はsocial backgroundに入れる
    return { ...defaultSummary, background_social: raw };
  }
}

interface FieldConfig {
  key: keyof StructuredSummary;
  label: string;
  placeholder: string;
  helpTitle: string;
  helpBody: string;
  helpExample: string;
  rows: number;
}

const backgroundFields: FieldConfig[] = [
  {
    key: 'background_social',
    label: '社会的背景（任意）',
    placeholder: '例：日本では中小企業の約6割が後継者不在であり、事業承継は喫緊の社会課題となっている。',
    helpTitle: '🌍 社会的背景とは？',
    helpBody: '世の中で今どんな問題が起きているか。ニュースや統計データを使って「なぜ今このテーマが重要か」を説明します。',
    helpExample: '日本では中小企業の約6割が後継者不在であり、年間約5万社が廃業している。地域経済への影響が懸念されている。',
    rows: 3,
  },
  {
    key: 'background_academic',
    label: '学術的背景（任意）',
    placeholder: '例：先行研究では税制が企業行動に影響することが示されているが、承継タイミングへの因果効果は未検証である。',
    helpTitle: '📚 学術的背景とは？',
    helpBody: 'これまでの研究で何がわかっていて、何がまだわかっていないか。「研究のギャップ（空白）」を示します。',
    helpExample: '先行研究では税制が企業行動に影響することが示されている（田中, 2020）。しかし、事業承継のタイミングへの因果効果は未検証である。',
    rows: 3,
  },
  {
    key: 'background_personal',
    label: '個人的動機（任意）',
    placeholder: '例：家業の承継問題を身近に経験し、税制の影響に関心を持った。',
    helpTitle: '💭 個人的動機とは？',
    helpBody: 'なぜ自分がこのテーマに興味を持ったのか。ゼミでの経験、ニュースで見たこと、身近な体験など。任意ですが、研究の動機を明確にできます。',
    helpExample: '家業が事業承継の問題に直面しており、税制がどの程度影響するのか疑問に思ったことがきっかけ。',
    rows: 2,
  },
];

const purposeFields: FieldConfig[] = [
  {
    key: 'purpose_what',
    label: '何を明らかにするか（任意）',
    placeholder: '例：事業承継税制が中小企業の承継タイミングに与える因果効果を明らかにする。',
    helpTitle: '🎯 何を明らかにするか',
    helpBody: 'この研究のゴールを一文で書きます。「〇〇が△△に与える影響を明らかにする」という形が書きやすいです。',
    helpExample: '事業承継税制の導入が、中小企業の事業承継タイミングに与える因果効果を定量的に明らかにする。',
    rows: 2,
  },
  {
    key: 'purpose_how',
    label: 'どうやって明らかにするか（任意）',
    placeholder: '例：2018年の税制改正を自然実験として、差分の差分法（DID）で分析する。',
    helpTitle: '🔧 どうやって明らかにするか',
    helpBody: 'どんな方法・データを使って検証するかの概要です。詳細は「分析の方法」セクションに書くので、ここでは概要だけでOKです。',
    helpExample: '2018年の事業承継税制の特例措置導入を自然実験として活用し、差分の差分法（DID）で因果効果を推定する。',
    rows: 2,
  },
  {
    key: 'purpose_scope',
    label: '対象範囲（任意）',
    placeholder: '例：対象は日本の中小企業（従業員300人以下）、期間は2010〜2023年。',
    helpTitle: '📐 対象範囲とは？',
    helpBody: 'どの国・地域、どの期間、どんな企業・人を対象にするか。研究の「境界線」を明確にします。',
    helpExample: '対象：日本の中小企業（中小企業基本法の定義に基づく）。期間：2010〜2023年。データ：企業活動基本調査。',
    rows: 2,
  },
  {
    key: 'intervention_group',
    label: '介入群（任意）',
    placeholder: '例：事業承継税制を適用した企業',
    helpTitle: '🧪 介入群とは？',
    helpBody: '実験や分析で「処置を受けた」グループのことです。政策の適用を受けた企業、新しい教育プログラムを受けた生徒など。',
    helpExample: '2018年以降に事業承継税制の特例措置を適用した中小企業（従業員300人以下）',
    rows: 2,
  },
  {
    key: 'control_group',
    label: '対照群（任意）',
    placeholder: '例：事業承継税制を適用していない企業',
    helpTitle: '🔬 対照群とは？',
    helpBody: '介入群と比較するための「処置を受けていない」グループです。介入群との違いを測定することで、政策や施策の効果を明らかにします。',
    helpExample: '事業承継税制を適用していない中小企業、または2018年以前に承継を完了した企業',
    rows: 2,
  },
];

const significanceFields: FieldConfig[] = [
  {
    key: 'significance_academic',
    label: '学術的意義（任意）',
    placeholder: '例：事業承継と税制の因果関係を初めて定量的に示すことで、租税研究に新たな知見を加える。',
    helpTitle: '🎓 学術的意義とは？',
    helpBody: '研究の世界にどう貢献するか。「初めて〇〇を明らかにする」「既存研究の△△を拡張する」などの形で書きます。',
    helpExample: '事業承継と税制の因果関係を定量的に示す初の研究となり、租税政策の効果測定に関する学術的知見を拡充する。',
    rows: 2,
  },
  {
    key: 'significance_social',
    label: '社会的意義（任意）',
    placeholder: '例：政策効果の実証により、より効果的な事業承継支援策の設計に貢献できる。',
    helpTitle: '🏛️ 社会的意義とは？',
    helpBody: '社会や政策にどう役立つか。研究結果が実際の問題解決にどうつながるかを書きます。',
    helpExample: '税制の効果を実証的に示すことで、政策立案者がより効果的な事業承継支援策を設計する際の根拠を提供する。',
    rows: 2,
  },
  {
    key: 'significance_practical',
    label: '実務的意義（任意）',
    placeholder: '例：中小企業経営者が承継計画を立てる際の判断材料を提供する。',
    helpTitle: '💼 実務的意義とは？',
    helpBody: '企業や個人が実際に活用できる点。任意ですが、研究の実用性を示せます。',
    helpExample: '中小企業の経営者や税理士が、事業承継の最適なタイミングを判断する際の参考情報を提供する。',
    rows: 2,
  },
];

export default function ResearchOverviewForm({
  onClose,
  onSuccess,
  editData,
}: ResearchOverviewFormProps) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [showHelp, setShowHelp] = useState<{ [key: string]: boolean }>({});

  const [title, setTitle] = useState('');
  const [structured, setStructured] = useState<StructuredSummary>({ ...defaultSummary });
  const [rq, setRq] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [nextMonthGoal, setNextMonthGoal] = useState('');

  useEffect(() => {
    if (editData) {
      // タイトルを設定
      setTitle(editData.title || '');
      
      // RQを設定
      setRq(editData.rq || '');
      
      // 仮説を設定
      const hyps = editData.hypotheses;
      if (Array.isArray(hyps) && hyps.length > 0) {
        setHypothesis(hyps.join('\n'));
      } else {
        setHypothesis('');
      }
      
      // 次月目標を設定
      setNextMonthGoal(editData.next_month_goal || '');

      // ★★★ 構造化データの読み込み優先順位を明確化 ★★★
      const metrics = editData.progress_metrics;
      let loadedStructured: StructuredSummary | null = null;

      // 1. progress_metricsがオブジェクト形式でstructured_summaryキーがある場合
      if (metrics && typeof metrics === 'object' && !Array.isArray(metrics)) {
        if (metrics.structured_summary?.data) {
          loadedStructured = { ...defaultSummary, ...metrics.structured_summary.data };
        }
      }
      
      // 2. progress_metricsが配列形式の場合
      if (!loadedStructured && Array.isArray(metrics)) {
        const structuredMetric = metrics.find((m: any) => m.type === 'structured_summary');
        if (structuredMetric?.data) {
          loadedStructured = { ...defaultSummary, ...structuredMetric.data };
        }
      }

      // 3. progress_metricsに構造化データがなければsummaryフィールドをパース
      if (!loadedStructured && editData.summary) {
        loadedStructured = parseSummary(editData.summary);
      }

      // 4. どこにもデータがなければデフォルト値
      if (!loadedStructured) {
        loadedStructured = { ...defaultSummary };
      }

      // ★★★ 確実にstateに反映 ★★★
      setStructured(loadedStructured);
    } else {
      // 新規作成時はすべてクリア
      setTitle('');
      setStructured({ ...defaultSummary });
      setRq('');
      setHypothesis('');
      setNextMonthGoal('');
    }
  }, [editData]);

  const toggleHelp = (field: string) => {
    setShowHelp((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updateField = (key: keyof StructuredSummary, value: string) => {
    setStructured((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!title.trim()) {
      setNotification({ type: 'error', message: '研究タイトルは必須です' });
      return;
    }

    setLoading(true);
    setNotification(null);

    try {
      const hypotheses = hypothesis
        .split('\n')
        .map((h) => h.trim())
        .filter((h) => h.length > 0);

      // ★★★ summaryにはJSONとして構造化データを保存（句点の連結は一切しない）★★★
      const summaryJson = JSON.stringify(structured);

      // ★★★ progress_metricsに構造化データを保存 ★★★
      const existingMetrics = editData?.progress_metrics ?? [];
      const structuredDataMetric = {
        type: 'structured_summary',
        data: structured,
        updated_at: new Date().toISOString(),
      };

      let updatedMetrics: any;
      if (Array.isArray(existingMetrics)) {
        updatedMetrics = [
          ...existingMetrics.filter((m: any) => m.type !== 'structured_summary'),
          structuredDataMetric,
        ];
      } else if (existingMetrics && typeof existingMetrics === 'object') {
        // オブジェクト形式の場合はresearch_procedureを保持しつつ更新
        updatedMetrics = {
          ...existingMetrics,
          structured_summary: structuredDataMetric,
        };
      } else {
        updatedMetrics = [structuredDataMetric];
      }

      const dataToSave = {
        user_id: user.id,
        title: title.trim(),
        theme: title.trim(),
        summary: summaryJson,
        rq: rq.trim() || null,
        hypotheses: hypotheses.length > 0 ? hypotheses : [],
        progress_metrics: updatedMetrics,
        next_month_goal: nextMonthGoal.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('research_overview')
          .update(dataToSave)
          .eq('id', editData.id)
          .eq('user_id', user.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message} (code: ${error.code})`);
        }
        setNotification({ type: 'success', message: 'テーマと目的を更新しました ✓' });
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else onClose();
        }, 1500);
      } else {
        const { error } = await supabase
          .from('research_overview')
          .insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message} (code: ${error.code})`);
        }

        try {
          const now = new Date().toISOString();
          const { data: existingProgress } = await supabase
            .from('research_step_progress')
            .select('*')
            .eq('user_id', user.id)
            .eq('step_id', 1)
            .maybeSingle();

          if (existingProgress) {
            await supabase
              .from('research_step_progress')
              .update({ status: 'completed', completed_at: now, started_at: existingProgress.started_at || now, updated_at: now })
              .eq('id', existingProgress.id);
          } else {
            await supabase
              .from('research_step_progress')
              .insert({ user_id: user.id, step_id: 1, status: 'completed', started_at: now, completed_at: now, updated_at: now });
          }
        } catch (progressError) {
          console.error('ステップ進捗の更新エラー:', progressError);
        }

        setNotification({ type: 'success', message: 'テーマと目的を保存しました ✓' });
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else onClose();
        }, 1500);
      }
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      const msg = err?.message || '不明なエラーが発生しました';
      setNotification({ type: 'error', message: `保存に失敗しました: ${msg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderFieldGroup = (
    groupTitle: string,
    groupIcon: string,
    groupColor: string,
    groupDesc: string,
    fields: FieldConfig[],
  ) => (
    <div className={`border rounded-xl overflow-hidden ${groupColor}`}>
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-inherit bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
            <i className={`${groupIcon} text-teal-600 text-sm sm:text-base`}></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">{groupTitle}</h3>
            <p className="text-xs text-slate-500">{groupDesc}</p>
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5 space-y-4 sm:space-y-5 bg-white">
        {fields.map((field) => (
          <div key={field.key}>
            <div className="flex items-center gap-2 mb-1.5">
              <label className="text-sm font-semibold text-slate-700">
                {field.label}
              </label>
              <button
                type="button"
                onClick={() => toggleHelp(field.key)}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-xs"></i>
              </button>
            </div>

            {showHelp[field.key] && (
              <div className="mb-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">{field.helpTitle}</p>
                <p className="mb-1">{field.helpBody}</p>
                <div className="bg-white border border-teal-200 rounded p-2 text-slate-600">
                  <span className="font-semibold">例：</span>
                  {field.helpExample}
                </div>
              </div>
            )}

            <textarea
              value={structured[field.key]}
              onChange={(e) => updateField(field.key, e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-normal text-slate-900 bg-white leading-relaxed placeholder:text-slate-400"
              rows={field.rows}
              placeholder={field.placeholder}
              maxLength={500}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {!user && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500"></i>
          ログインが必要です。ログインしてから再度お試しください。
        </div>
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-lightbulb-line text-teal-600"></i>
          {editData ? 'テーマと目的を編集' : 'テーマと目的を設定'}
        </h2>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        {/* 研究タイトル */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-semibold text-slate-700">
              研究タイトル <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => toggleHelp('title')}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-xs"></i>
            </button>
          </div>
          {showHelp.title && (
            <div className="mb-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-xs text-slate-700">
              <p className="font-semibold mb-1">📌 研究タイトルとは？</p>
              <p className="mb-1">
                研究の内容を端的に表すタイトルです。「〇〇が△△に与える影響」のような形が一般的です。
              </p>
              <div className="bg-white border border-teal-200 rounded p-2 text-slate-600">
                <span className="font-semibold">例：</span>
                事業承継税制が中小企業の承継タイミングに与える影響：パネルデータによる実証分析
              </div>
            </div>
          )}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-normal text-slate-900 bg-white placeholder:text-slate-400"
            placeholder="例：事業承継税制が中小企業の承継タイミングに与える影響"
            required
          />
        </div>

        {/* 背景 */}
        {renderFieldGroup(
          '背景（なぜこのテーマを選んだのか）',
          'ri-earth-line',
          'border-teal-200',
          '社会的・学術的に、なぜこの研究が必要なのかを説明します',
          backgroundFields,
        )}

        {/* 目的 */}
        {renderFieldGroup(
          '目的（この研究で何を明らかにするか）',
          'ri-focus-3-line',
          'border-emerald-200',
          '研究のゴールと、そこに至るアプローチを明確にします',
          purposeFields,
        )}

        {/* 意味 */}
        {renderFieldGroup(
          '意義（この研究がどう役立つか）',
          'ri-award-line',
          'border-teal-200',
          '研究成果が学術・社会・実務にどう貢献するかを示します',
          significanceFields,
        )}

        {/* リサーチクエスチョン */}
        <div className="border border-amber-200 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                <i className="ri-question-answer-line text-amber-600 text-sm sm:text-base"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">リサーチクエスチョン（RQ）</h3>
                <p className="text-xs text-slate-500">この研究で答えたい「問い」を疑問文で書きます</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 bg-white">
            <div className="flex items-center gap-2 mb-1.5">
              <label className="text-sm font-semibold text-slate-700">
                RQ（任意）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('rq')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-xs"></i>
              </button>
            </div>
            {showHelp.rq && (
              <div className="mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">❓ リサーチクエスチョンとは？</p>
                <p className="mb-1">
                  研究全体を通じて答えたい「問い」です。「〇〇は△△にどのような影響を与えるか？」という疑問文で書きます。
                </p>
                <div className="bg-white border border-amber-200 rounded p-2 text-slate-600">
                  <span className="font-semibold">例：</span>
                  事業承継税制は中小企業の承継タイミングにどのような影響を与えるか？
                </div>
              </div>
            )}
            <textarea
              value={rq}
              onChange={(e) => setRq(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm font-normal text-slate-900 bg-white placeholder:text-slate-400"
              rows={2}
              placeholder="〇〇は△△にどのような影響を与えるか？"
              maxLength={500}
            />
          </div>
        </div>

        {/* 仮説 */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-semibold text-slate-700">仮説（任意）</label>
            <button
              type="button"
              onClick={() => toggleHelp('hypothesis')}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-xs"></i>
            </button>
          </div>
          {showHelp.hypothesis && (
            <div className="mb-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-xs text-slate-700">
              <p className="font-semibold mb-1">💡 仮説とは？</p>
              <p className="mb-1">RQに対する「予想される答え」です。1行に1つずつ書いてください。</p>
              <div className="bg-white border border-teal-200 rounded p-2 text-slate-600">
                <span className="font-semibold">例：</span><br />
                事業承継税制の適用企業は、非適用企業より承継が早い<br />
                税制の効果は企業規模が小さいほど大きい
              </div>
            </div>
          )}
          <textarea
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-normal text-slate-900 bg-white placeholder:text-slate-400"
            rows={3}
            placeholder="1行に1つずつ仮説を記入&#10;例：事業承継税制の適用企業は、非適用企業より承継が早い&#10;例：税制の効果は企業規模が小さいほど大きい"
            maxLength={500}
          />
          <p className="text-xs text-slate-400 mt-1">※ 1行に1つずつ記入してください</p>
        </div>

        {/* 次の目標 */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-semibold text-slate-700">次の1ヶ月の最重要目標（任意）</label>
            <button
              type="button"
              onClick={() => toggleHelp('goal')}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-xs"></i>
            </button>
          </div>
          {showHelp.goal && (
            <div className="mb-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-xs text-slate-700">
              <p className="font-semibold mb-1">🚩 次の目標とは？</p>
              <p>来月までに必ず達成したい1つの目標を書きます。</p>
              <div className="bg-white border border-teal-200 rounded p-2 text-slate-600 mt-1">
                <span className="font-semibold">例：</span>
                データクリーニングを完了し、記述統計表を作成する
              </div>
            </div>
          )}
          <textarea
            value={nextMonthGoal}
            onChange={(e) => setNextMonthGoal(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-normal text-slate-900 bg-white placeholder:text-slate-400"
            rows={2}
            placeholder="例：データクリーニングを完了し、記述統計表を作成する"
            maxLength={500}
          />
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading || !user}
            className="w-full sm:flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                保存中...
              </span>
            ) : editData ? '更新する' : '保存する'}
          </button>
        </div>
      </form>
    </div>
  );
}